import { forwardRef, useContext } from 'react'
import { TabsContext } from './tabs-context'
import { makeContentId, makeTriggerId } from '../constants'
import { StyledTabsSkeleton, StyledTabsTrigger } from '../styled-components'
import PropTypes from 'prop-types'

const TabsTrigger = forwardRef((props, forwardedRef) => {
  const { children, value, disabled = false, ...triggerProps } = props
  const context = useContext(TabsContext)

  const triggerId = makeTriggerId(context.baseId, value)
  const contentId = makeContentId(context.baseId, value)

  const isSelected = value === context.value
  return (
    <StyledTabsTrigger
      type="button"
      role="tab"
      aria-selected={isSelected}
      aria-controls={contentId}
      data-state={isSelected ? 'active' : 'inactive'}
      data-disabled={disabled ? '' : undefined}
      data-loading={context.loading ? '' : undefined}
      disabled={disabled || context.disabled || context.loading}
      id={triggerId}
      {...triggerProps}
      ref={forwardedRef}
      onMouseDown={(event) => {
        props.onMouseDown && props.onMouseDown()
        // only call handler if it's the left button (mousedown gets triggered by all mouse buttons)
        // but not when the control key is pressed (avoiding MacOS right click)
        if (!disabled && event.button === 0 && event.ctrlKey === false) {
          context.onValueChange(value)
        } else {
          // prevent focus to avoid accidental activation
          event.preventDefault()
        }
      }}
      onKeyDown={(event) => {
        props.onKeyDown && props.onKeyDown()
        if ([' ', 'Enter'].includes(event.key)) context.onValueChange(value)
      }}
      onFocus={() => {
        props.onFocus && props.onFocus()
        // handle "automatic" activation if necessary
        // ie. activate tab following focus
        const isAutomaticActivation = context.activationMode !== 'manual'
        if (!isSelected && !disabled && isAutomaticActivation) {
          context.onValueChange(value)
        }
      }}
    >
      {context.loading
        ? (
        <StyledTabsSkeleton>{children}</StyledTabsSkeleton>
          )
        : (
        <>{children}</>
          )}
    </StyledTabsTrigger>
  )
})

TabsTrigger.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool
}

TabsTrigger.displayName = 'TabsTrigger'
export default TabsTrigger
